import { icons } from '@/components/icons';
import { renderCalendar, viewOf } from '@/components/calendar';
import { BookingError, createItinerary } from '@/api/booking';
import { getProduct } from '@/api/products';
import { selectionStore } from '@/lib/store';
import { escapeHtml, formatDate, formatPrice } from '@/lib/format';
import {
  bookableDateSet,
  maxStartDate,
  minStartDate,
  nextBookableDate,
  todayIso,
} from '@/lib/availability';
import type { Product } from '@/api/types';
import type { Screen } from '@/router/router';

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function showToast(host: HTMLElement, message: string): void {
  let toast = host.querySelector<HTMLElement>('.toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    host.appendChild(toast);
  }
  toast.textContent = message;
  requestAnimationFrame(() => toast?.classList.add('show'));
  window.setTimeout(() => toast?.classList.remove('show'), 2400);
}

export function createCustomizeScreen(): Screen {
  let rootEl!: HTMLElement;
  let sheetEl!: HTMLElement;
  let onBack!: () => void;
  let getParams!: () => Record<string, string>;
  let goToCheckout!: (itineraryId: string) => void;
  let calendarView: string | undefined; // yyyy-mm currently visible in the calendar

  function compute(): {
    product: Product | undefined;
    startDate: string;
    adults: number;
    rooms: number;
    total: number | null;
    currency: string;
  } {
    const sel = selectionStore.get();
    const product = sel.product;
    const total = product?.price != null ? product.price * sel.adults : null;
    return {
      product,
      startDate: sel.startDate,
      adults: sel.adults,
      rooms: sel.rooms,
      total,
      currency: product?.currency ?? 'EUR',
    };
  }

  function rerender(): void {
    const { product, startDate, adults, rooms, total, currency } = compute();
    if (!product) {
      sheetEl.innerHTML = `
        <div class="sheet-head">
          <div class="sheet-handle"></div>
          <div class="sheet-head-inner">
            <div class="sheet-head-text">
              <div class="sheet-title">Personalizza il viaggio</div>
              <div class="sheet-sub">Nessun prodotto selezionato.</div>
            </div>
          </div>
        </div>
        <div class="sheet-footer">
          <button class="btn btn-outline" data-action="close" type="button">Chiudi</button>
        </div>
      `;
      wireSheet();
      return;
    }

    const minDate = minStartDate(product);
    const maxDate = maxStartDate(product) ?? '';
    const maxPaxPerRoom = product.maxPaxPerRoom ?? 4;
    const maxPax = product.maxPax ?? 8;
    const minPax = product.minPax ?? 1;
    const dateInPast = startDate < todayIso();
    const availSet = bookableDateSet(product);
    const view = calendarView ?? viewOf(startDate);
    const isAvailable = availSet.has(startDate);

    sheetEl.innerHTML = `
      <div class="sheet-head">
        <div class="sheet-handle"></div>
        <button class="sheet-close" data-action="close" type="button" aria-label="Chiudi">×</button>
        <div class="sheet-head-inner">
          <div class="srow-icon">${icons.clock(16, '#111111')}</div>
          <div class="sheet-head-text">
            <div class="sheet-title">Personalizza il viaggio</div>
            <div class="sheet-sub">${escapeHtml(product.title)}</div>
          </div>
        </div>
      </div>

      <div class="sheet-body" data-role="sheet-body">
        <div class="srow srow-compact">
          <div class="srow-icon">${icons.users(16, '#111111')}</div>
          <div>
            <div class="srow-lbl">Adulti</div>
            <div class="srow-val">${adults} ${adults === 1 ? 'persona' : 'persone'}</div>
          </div>
          <div class="srow-control">
            <button class="qty-btn" data-action="adults-minus" type="button" aria-label="-" ${adults <= minPax ? 'disabled' : ''}>−</button>
            <span class="qty-val">${adults}</span>
            <button class="qty-btn qty-btn-plus" data-action="adults-plus" type="button" aria-label="+" ${adults >= maxPax ? 'disabled' : ''}>+</button>
          </div>
        </div>

        <div class="srow srow-compact">
          <div class="srow-icon">${icons.bedDouble(16, '#111111')}</div>
          <div>
            <div class="srow-lbl">Camere</div>
            <div class="srow-val">${rooms} ${rooms === 1 ? 'camera' : 'camere'}</div>
          </div>
          <div class="srow-control">
            <button class="qty-btn" data-action="rooms-minus" type="button" aria-label="-" ${rooms <= 1 ? 'disabled' : ''}>−</button>
            <span class="qty-val">${rooms}</span>
            <button class="qty-btn qty-btn-plus" data-action="rooms-plus" type="button" aria-label="+" ${rooms >= Math.ceil(adults / Math.max(1, maxPaxPerRoom)) + 2 ? 'disabled' : ''}>+</button>
          </div>
        </div>

        <div class="srow srow-compact srow-date-summary">
          <div class="srow-icon">${icons.calendar(16, '#111111')}</div>
          <div>
            <div class="srow-lbl">Data di partenza</div>
            <div class="srow-val">${formatDate(startDate)}</div>
          </div>
        </div>

        ${renderCalendar({
          value: startDate,
          available: availSet,
          min: minDate,
          max: maxDate || undefined,
          view,
        })}

        ${
          dateInPast
            ? `<div class="sheet-note sheet-note-err">La data selezionata è nel passato. Scegli una data futura.</div>`
            : !isAvailable
              ? `<div class="sheet-note">Verifichiamo la disponibilità per questa data al checkout.</div>`
              : ''
        }
      </div>

      <div class="sheet-footer">
        <div class="sheet-price-block">
          <span class="sheet-price-lbl">Totale stimato</span>
          <strong class="sheet-price-amt">${total != null ? formatPrice(total, currency) : 'su richiesta'}</strong>
        </div>
        <button class="btn btn-pill-lime" data-action="book" type="button" ${dateInPast ? 'disabled' : ''}>
          <span>Prenota ora</span>
          <span class="btn-pill-arrow" aria-hidden="true">→</span>
        </button>
      </div>
    `;

    wireSheet();
  }

  function wireSheet(): void {
    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="close"]')
      ?.addEventListener('click', () => onBack());

    sheetEl.querySelectorAll<HTMLButtonElement>('[data-action="prev"], [data-action="next"]').forEach(
      (btn) => {
        btn.addEventListener('click', () => {
          const v = btn.dataset.view;
          if (v) {
            calendarView = v;
            rerender();
          }
        });
      },
    );

    sheetEl.querySelectorAll<HTMLButtonElement>('[data-action="pick"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const d = btn.dataset.date;
        if (!d) return;
        calendarView = viewOf(d);
        selectionStore.set({ startDate: d });
        rerender();
      });
    });

    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="adults-minus"]')
      ?.addEventListener('click', () => {
        const cur = selectionStore.get();
        selectionStore.set({ adults: clamp(cur.adults - 1, 1, 50) });
        rerender();
      });
    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="adults-plus"]')
      ?.addEventListener('click', () => {
        const cur = selectionStore.get();
        selectionStore.set({ adults: clamp(cur.adults + 1, 1, 50) });
        rerender();
      });
    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="rooms-minus"]')
      ?.addEventListener('click', () => {
        const cur = selectionStore.get();
        selectionStore.set({ rooms: clamp(cur.rooms - 1, 1, 20) });
        rerender();
      });
    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="rooms-plus"]')
      ?.addEventListener('click', () => {
        const cur = selectionStore.get();
        selectionStore.set({ rooms: clamp(cur.rooms + 1, 1, 20) });
        rerender();
      });

    sheetEl
      .querySelector<HTMLButtonElement>('[data-action="book"]')
      ?.addEventListener('click', () => {
        void book();
      });
  }

  function setLoading(active: boolean): void {
    let overlay = rootEl.querySelector<HTMLElement>('.spinner-overlay');
    if (active) {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'spinner-overlay';
        overlay.innerHTML = '<div class="spinner"></div>';
        rootEl.appendChild(overlay);
      }
    } else if (overlay) {
      overlay.remove();
    }
  }

  async function book(): Promise<void> {
    const { product, startDate, adults, rooms } = compute();
    if (!product) return;
    setLoading(true);
    try {
      const { itineraryId } = await createItinerary({
        productId: product.id,
        startDate,
        adults,
        rooms,
      });
      setLoading(false);
      goToCheckout(itineraryId);
    } catch (err) {
      setLoading(false);
      let msg = 'Impossibile creare il carrello.';
      if (err instanceof BookingError) {
        msg = err.message || msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      showToast(rootEl, msg);
    }
  }

  return {
    id: 'customize',
    async mount({ el, back, go, params }) {
      rootEl = el;
      onBack = back;
      getParams = params;
      goToCheckout = (itineraryId) =>
        go('checkout', { params: { itineraryId } });
      rootEl.id = 'wcu';

      rootEl.innerHTML = `
        <div class="cu-backdrop" data-action="close"></div>
        <div class="cu-sheet" data-role="sheet"></div>
      `;

      sheetEl = rootEl.querySelector<HTMLElement>('[data-role="sheet"]')!;
      rootEl
        .querySelector<HTMLElement>('[data-action="close"]')
        ?.addEventListener('click', () => onBack());
    },
    async onEnter() {
      // Deep-link support: if the user landed on /p/:id/customize without going
      // through the listing, the product is missing from the selectionStore.
      const productIdParam = getParams().productId;
      const sel = selectionStore.get();
      if (productIdParam && sel.product?.id !== productIdParam) {
        try {
          const p = await getProduct(productIdParam);
          selectionStore.set({ product: p });
        } catch {
          // ignore — rerender will show the empty state
        }
      }

      // Self-heal: if the current selection is on a past date bump it to the
      // next bookable date — or today as a last resort.
      const sel2 = selectionStore.get();
      const today = todayIso();
      if (sel2.startDate < today) {
        const fallback = nextBookableDate(sel2.product) ?? today;
        selectionStore.set({ startDate: fallback });
      }
      // Reset the calendar view to the month containing the current selection
      // so every reopen starts from the active date.
      calendarView = viewOf(selectionStore.get().startDate);
      rerender();
    },
  };
}
